import {
  getInvalidTpSlOrdersToCancel,
} from 'client/hooks/execute/cancelOrder/useExecuteCancelInvalidTpSlOrders';
import { useSubaccountIsolatedPositions } from 'client/hooks/query/subaccount/isolatedPositions/useSubaccountIsolatedPositions';
import { useSubaccountSummary } from 'client/hooks/query/subaccount/subaccountSummary/useSubaccountSummary';
import { useSubaccountLinkedSigner } from 'client/hooks/query/subaccount/useSubaccountLinkedSigner';
import { useSubaccountOpenTriggerOrders } from 'client/hooks/query/subaccount/useSubaccountOpenTriggerOrders';
import { useSuppressedOrphanedTpSlDigests } from 'client/modules/trading/hooks/useSuppressedOrphanedTpSlDigests';
import { usePrimaryChainVertexClient } from '@vertex-protocol/react-client';
import { useMemo } from 'react';
import { zeroAddress } from 'viem';
import { CancellableOrder } from 'client/hooks/execute/cancelOrder/types';

export interface OrphanedTpSlStatus {
  /**
   * Orders that are orphaned (position closed / side changed) and not currently
   * suppressed. Empty when positions/orders haven't loaded yet.
   */
  orphanedOrders: CancellableOrder[];
  /**
   * True when the backend has a linked signer registered but the local vertex
   * client does not have a linked signer wallet client configured. This happens
   * when the 1CT session key has expired or was cleared locally.
   *
   * When true, attempts to cancel trigger orders will be rejected by the trigger
   * service ("stale linked signer"), so we should skip the cancellation.
   */
  signerIsStale: boolean;
  /**
   * Number of digests currently in the suppressed set.
   */
  suppressedDigestCount: number;
}

/**
 * Computes the current set of orphaned TP/SL orders and whether the local
 * linked signer is out of sync with the backend, which would cause cancellation
 * attempts to fail.
 */
export function useOrphanedTpSlStatus(): OrphanedTpSlStatus {
  const vertexClient = usePrimaryChainVertexClient();
  const { data: backendLinkedSigner } = useSubaccountLinkedSigner();
  const { data: currentSubaccountSummary } = useSubaccountSummary();
  const { data: isolatedPositions } = useSubaccountIsolatedPositions();
  const { data: openTriggerOrders } = useSubaccountOpenTriggerOrders();
  const { suppressedDigests, isDigestSuppressed } =
    useSuppressedOrphanedTpSlDigests();

  const signerIsStale = useMemo(() => {
    if (!backendLinkedSigner) {
      // Not yet loaded — assume not stale
      return false;
    }
    const backendSignerAddress = backendLinkedSigner.signer.toLowerCase();
    const backendHasSigner = backendSignerAddress !== zeroAddress;
    const localHasSigner =
      vertexClient?.context.linkedSignerWalletClient != null;

    return backendHasSigner && !localHasSigner;
  }, [backendLinkedSigner, vertexClient]);

  const orphanedOrders = useMemo(() => {
    if (
      !currentSubaccountSummary ||
      !isolatedPositions ||
      !openTriggerOrders
    ) {
      return [];
    }

    const allInvalid = getInvalidTpSlOrdersToCancel({
      balances: currentSubaccountSummary.balances,
      isolatedPositions,
      openTriggerOrders,
    });

    // Filter out orders that have been suppressed after signer-stale failures
    return allInvalid.filter(
      (order) => !isDigestSuppressed(order.digest),
    );
  }, [
    currentSubaccountSummary,
    isolatedPositions,
    openTriggerOrders,
    isDigestSuppressed,
  ]);

  const suppressedDigestCount = Object.keys(suppressedDigests).length;

  return { orphanedOrders, signerIsStale, suppressedDigestCount };
}
